import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { analyticsService } from '../lib/analytics/analyticsService';
import { sessionTracker } from '../lib/analytics/sessionTracker';
import { logger } from '../lib/logger';
import type { 
  EventType, 
  EventName,
  ItemActionName,
  InteractionEventName,
  FilterEventName
} from '../lib/analytics/types';

interface UseAnalyticsOptions {
  sectionId?: string;
  sectionName?: string;
  sectionType?: string;
}

export function useAnalytics(options: UseAnalyticsOptions = {}) {
  const location = useLocation();
  
  /**
   * Track a page view
   */
  const trackPageView = useCallback((path: string = location.pathname) => {
    analyticsService.trackEvent({
      event_type: 'navigation',
      event_name: 'page_view',
      event_data: {
        path,
        page_title: document.title,
        query_params: location.search,
        hash: location.hash,
        section_id: options.sectionId,
        section_name: options.sectionName,
        section_type: options.sectionType,
        timestamp: new Date().toISOString()
      }
    });
    
    // Increment page view in session tracker
    sessionTracker.incrementPageView();
    
    logger.debug('Page view tracked', {
      category: 'Analytics',
      data: {
        path,
        title: document.title,
        section: options.sectionId,
        timestamp: new Date().toISOString()
      }
    });
  }, [location, options.sectionId, options.sectionName, options.sectionType]);

  /**
   * Track a generic event with the new structure
   */
  const trackEvent = useCallback((
    eventType: EventType,
    eventName: EventName,
    eventData: Record<string, any> = {}
  ) => {
    // Format the event data according to the agreed structure
    const formattedEventData = {
      // Section information
      section_id: eventData.section_id || options.sectionId,
      section_name: eventData.section_name || options.sectionName,
      section_type: eventData.section_type || options.sectionType,
      
      // Content information
      content_id: eventData.content_id || eventData.item_id,
      content_instance_id: eventData.content_instance_id || eventData.event_id,
      content_type: eventData.content_type || eventData.item_type,
      content_name: eventData.content_name || eventData.item_name,
      
      // Date and time information for events
      date: eventData.date,
      time: eventData.time,
      
      // Category and type
      category: eventData.category,
      type: eventData.type,
      
      // Action information
      action: eventData.action,
      source_view: eventData.source_view || eventData.source,
      target_url: eventData.target_url || eventData.url,
      
      // Include any other data that was passed
      ...eventData,
      
      // Add timestamp
      timestamp: new Date().toISOString(),
    };
    
    analyticsService.trackEvent({
      event_type: eventType,
      event_name: eventName,
      event_data: formattedEventData
    });
    
    // Increment interaction in session tracker
    sessionTracker.incrementInteraction();
    
    logger.debug('Event tracked', {
      category: 'Analytics',
      data: {
        eventType,
        eventName,
        eventData: formattedEventData,
        timestamp: new Date().toISOString()
      }
    });
  }, [options.sectionId, options.sectionName, options.sectionType]);

  /**
   * Track a section view with the new structure
   */
  const trackSectionView = useCallback((
    viewType: 'list' | 'map',
    additionalData: Record<string, any> = {}
  ) => {
    if (!options.sectionId) {
      logger.warn('Missing sectionId for trackSectionView', {
        category: 'Analytics'
      });
      return;
    }
    
    trackEvent('content_view', 'section_view', {
      section_id: options.sectionId,
      section_name: options.sectionName,
      section_type: options.sectionType,
      view_type: viewType,
      source: 'section',
      ...additionalData
    });
    
    logger.debug('Section view tracked', {
      category: 'Analytics',
      data: {
        sectionId: options.sectionId,
        sectionType: options.sectionType,
        viewType,
        timestamp: new Date().toISOString()
      }
    });
  }, [options.sectionId, options.sectionName, options.sectionType, trackEvent]);

  /**
   * Track an item action with the new structure
   */
  const trackItemAction = useCallback((
    itemId: string,
    action: ItemActionName,
    itemType: string,
    additionalData: Record<string, any> = {}
  ) => {
    if (!options.sectionId) {
      logger.warn('Missing sectionId for trackItemAction', {
        category: 'Analytics'
      });
      return;
    }
    
    trackEvent('item_action', action, {
      section_id: options.sectionId,
      section_name: options.sectionName,
      section_type: options.sectionType,
      
      content_id: itemId,
      content_instance_id: additionalData.event_id || additionalData.show_id || itemId,
      content_type: itemType,
      content_name: additionalData.item_name,
      
      date: additionalData.date,
      time: additionalData.hour || additionalData.time,
      
      category: additionalData.category,
      type: additionalData.type,
      
      action: action,
      source_view: additionalData.source,
      target_url: additionalData.url,
      
      ...additionalData
    });
    
    logger.debug('Item action tracked', {
      category: 'Analytics',
      data: {
        sectionId: options.sectionId,
        itemId,
        action,
        itemType,
        timestamp: new Date().toISOString()
      }
    });
  }, [options.sectionId, options.sectionName, options.sectionType, trackEvent]);

  /**
   * Track an interaction with the new structure
   */
  const trackInteraction = useCallback((
    interactionName: InteractionEventName,
    interactionData: Record<string, any> = {}
  ) => {
    trackEvent('interaction', interactionName, {
      section_id: options.sectionId,
      section_name: options.sectionName,
      section_type: options.sectionType,
      
      content_id: interactionData.item_id,
      content_name: interactionData.item_name,
      content_type: interactionData.item_type,
      
      action: interactionName,
      source_view: interactionData.source,
      
      ...interactionData
    });
    
    logger.debug('Interaction tracked', {
      category: 'Analytics',
      data: {
        interactionName,
        interactionData,
        timestamp: new Date().toISOString()
      }
    });
  }, [options.sectionId, options.sectionName, options.sectionType, trackEvent]);

  /**
   * Track a filter with the new structure
   */
  const trackFilter = useCallback((
    filterName: FilterEventName,
    filterValue: string,
    additionalData: Record<string, any> = {}
  ) => {
    trackEvent('filter', filterName, {
      section_id: options.sectionId,
      section_name: options.sectionName,
      section_type: options.sectionType,
      
      filter_value: filterValue,
      filter_type: filterName,
      
      ...additionalData
    });
    
    logger.debug('Filter tracked', {
      category: 'Analytics',
      data: {
        filterName,
        filterValue,
        timestamp: new Date().toISOString()
      }
    });
  }, [options.sectionId, options.sectionName, options.sectionType, trackEvent]);

  /**
   * Track an error with the new structure
   */
  const trackError = useCallback((
    errorName: string,
    error: Error | string,
    additionalData: Record<string, any> = {}
  ) => {
    const errorMessage = error instanceof Error ? error.message : error;
    const errorStack = error instanceof Error ? error.stack : undefined;
    
    trackEvent('error', errorName, {
      section_id: options.sectionId,
      section_name: options.sectionName,
      section_type: options.sectionType,
      
      error_message: errorMessage,
      error_stack: errorStack,
      error_type: error instanceof Error ? error.name : 'string',
      
      content_id: additionalData.item_id,
      content_name: additionalData.item_name,
      
      ...additionalData
    });
    
    logger.error(`Analytics error: ${errorName}`, {
      category: 'Analytics',
      data: {
        error,
        additionalData,
        timestamp: new Date().toISOString()
      }
    });
  }, [options.sectionId, options.sectionName, options.sectionType, trackEvent]);

  return {
    trackPageView,
    trackEvent,
    trackSectionView,
    trackItemAction,
    trackInteraction,
    trackFilter,
    trackError,
    isEnabled: analyticsService.isAnalyticsEnabled()
  };
}